// What the signed-in user may do in the current project.
//
// Loaded once per project from /api/access-control/users/me/access:
// the project role, the permission list, and one row per backend route saying
// whether this user may call it. The app uses it to hide controls and to skip
// calls the server would refuse. The server check stays the real one, so
// while access is not loaded (or could not be loaded) everything is allowed
// here and the server decides.

import { useSyncExternalStore } from 'react';

// Same fallback as services/api.js (not imported from there: api.js imports this file).
const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

export const NO_ACCESS_MESSAGE = 'You do not have access to this.';

const LOAD_TIMEOUT_MS = 8000;

const UNKNOWN = {
  status: 'unknown',
  role: null,
  permissions: null,
  routes: [],
};

let state = UNKNOWN;
let loadedFor = null;
const listeners = new Set();

const setState = (next) => {
  state = next;
  listeners.forEach((listener) => listener());
};

const trimSlash = (path) => (path.length > 1 ? path.replace(/\/+$/, '') : path);

// "/meetings/{meeting_id}" -> /^\/meetings\/[^/]+$/ ; "{ref:path}" spans slashes.
export const pathToRegExp = (template) => {
  const source = trimSlash(template)
    .split(/(\{[^}]+\})/)
    .map((part) => {
      if (!part.startsWith('{'))
        return part.replace(/[.*+?^$()|[\]\\]/g, '\\$&');
      return part.endsWith(':path}') ? '.+' : '[^/]+';
    })
    .join('');
  return new RegExp(`^${source}$`);
};

const compileRoutes = (rows) =>
  (rows || []).map(([methods, path, allowed]) => ({
    methods: methods.split(','),
    pattern: pathToRegExp(path),
    allowed,
  }));

export const setAccess = (data) =>
  setState({
    status: 'ready',
    role: data?.project_role || null,
    permissions: new Set(data?.permissions || []),
    routes: compileRoutes(data?.routes),
  });

export const clearAccess = () => {
  loadedFor = null;
  setState(UNKNOWN);
};

export const loadAccess = async (token) => {
  if (!token) return clearAccess();
  if (loadedFor === token && state.status !== 'unknown') return undefined;
  loadedFor = token;
  setState({ ...UNKNOWN, status: 'loading' });
  // The app waits for this before it shows anything, so it must not hang.
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), LOAD_TIMEOUT_MS);
  try {
    const response = await fetch(
      `${API_URL}/api/access-control/users/me/access`,
      {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
        signal: abort.signal,
      },
    );
    if (!response.ok) throw new Error(String(response.status));
    const body = await response.json();
    if (loadedFor === token) setAccess(body.data);
  } catch (error) {
    // Not loaded: allow everything here and let the server decide.
    if (loadedFor === token) setState({ ...UNKNOWN, status: 'failed' });
  } finally {
    clearTimeout(timer);
  }
  return undefined;
};

export const can = (permission) =>
  state.permissions === null || state.permissions.has(permission);

// Whether the server would accept this call. Unknown routes are allowed.
export const mayCall = (url, method = 'GET') => {
  if (!state.routes.length || !API_URL) return true;
  const text = String(url);
  if (!text.startsWith(API_URL)) return true;
  const path = trimSlash(text.slice(API_URL.length).split(/[?#]/)[0] || '/');
  const verb = String(method || 'GET').toUpperCase();
  const route = state.routes.find(
    (row) => row.methods.includes(verb) && row.pattern.test(path),
  );
  return route ? route.allowed : true;
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

// { status, role, can(permission) } and re-renders when access changes.
export const useAccess = () => {
  const snapshot = useSyncExternalStore(subscribe, () => state);
  return {
    status: snapshot.status,
    role: snapshot.role,
    can: (permission) =>
      snapshot.permissions === null || snapshot.permissions.has(permission),
  };
};

// Development only: name every call the guard answers itself, so a screen
// that offers something the role may not do shows up in the console.
const reportRefused = (method, url) => {
  if (process.env.NODE_ENV === 'production') return;
  const call = `${String(method).toUpperCase()} ${url}`;
  // Also kept on window, from page load on, for checking a role by hand.
  (window.__accessRefused = window.__accessRefused || []).push(call);
  // eslint-disable-next-line no-console
  console.warn(`[access] refused ${call}`);
};

const refused = () =>
  new Response(
    JSON.stringify({ detail: NO_ACCESS_MESSAGE, error: NO_ACCESS_MESSAGE }),
    { status: 403, headers: { 'Content-Type': 'application/json' } },
  );

// Every request the app makes goes through fetch or axios. A call the role
// may not make is answered here with the same 403 the server would give,
// without being sent.
export const installAccessGuard = (axios) => {
  if (typeof window !== 'undefined' && window.fetch && !window.fetch.guarded) {
    const nativeFetch = window.fetch.bind(window);
    const guardedFetch = (input, init) => {
      const url = typeof input === 'string' ? input : input?.url || input;
      const method = init?.method || input?.method || 'GET';
      if (mayCall(url, method)) return nativeFetch(input, init);
      reportRefused(method, url);
      return Promise.resolve(refused());
    };
    guardedFetch.guarded = true;
    window.fetch = guardedFetch;
  }
  axios?.interceptors.request.use((config) => {
    const url = /^https?:/.test(config.url || '')
      ? config.url
      : `${config.baseURL || ''}${config.url || ''}`;
    if (mayCall(url, config.method)) return config;
    reportRefused(config.method || 'GET', url);
    const error = new Error(NO_ACCESS_MESSAGE);
    error.config = config;
    error.isAxiosError = true;
    error.response = {
      status: 403,
      data: { detail: NO_ACCESS_MESSAGE },
      headers: {},
      config,
    };
    return Promise.reject(error);
  });
};
